import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import CloseIcon from '@mui/icons-material/Close';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Card,
    CardContent,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { Notify } from 'notiflix';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { makeGift } from '../../../data/api/gifts/GiftsRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';

const nowDate = new Date(Date.now());
const minDate = `${nowDate.getFullYear()}-${nowDate.getMonth() + 1 > 9 ? nowDate.getMonth() + 1 : '0' + (nowDate.getMonth() + 1)}-${nowDate.getDate()}`;

const MOVES_OPTIONS = [
    { value: 'roulette', label: 'Ruleta', sub: 'Una jugada aleatoria' },
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
        value: n,
        label: `${n} jugada${n > 1 ? 's' : ''}`,
        sub: `Entrega ${n} jugada${n > 1 ? 's' : ''} fija${n > 1 ? 's' : ''}`,
    })),
];

const USERS_OPTIONS = [
    { value: 'all', label: 'Todos los usuarios', sub: 'Visible para cualquier jugador' },
    { value: 'woutmoves', label: 'Sin jugadas', sub: 'Solo a jugadores con 0 jugadas' },
    { value: 'wmoves', label: 'Con jugadas', sub: 'Solo a jugadores que ya tienen jugadas' },
];

/**
 * Modal de creación de regalos.
 *
 * Coherente con el lenguaje visual del resto de modales del módulo
 * (PrizePayout, EditPayout, PayPayout): header con icono dorado, tarjeta
 * blanca centrada, secciones internas con borde tenue, botón con
 * gradiente azul.
 */
const GiftsMake = ({ handleClose }) => {
    const { token } = useIndex();
    const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
        defaultValues: {
            moves: 'roulette',
            users: 'all',
            expires_at: '',
        },
    });
    const [loading, setLoading] = useState(false);

    const onSubmit = async (dataForm) => {
        setLoading(true);
        try {
            const { data } = await makeGift(token, dataForm);
            if (data?.success) {
                Notify.success(data?.message ?? 'Regalo creado correctamente.');
                reset();
                handleClose();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Center>
            <Card sx={{ width: '100%', maxWidth: 720, maxHeight: '92vh', overflow: 'auto', borderRadius: '22px' }}>
                <CardContent>
                    <Header>
                        <Box className="title-wrap">
                            <Box className="icon-box">
                                <CardGiftcardOutlinedIcon />
                            </Box>
                            <Box>
                                <Typography variant="h6">Crear regalo</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Otorga jugadas o premios sorpresa a tus usuarios.
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleClose} size="small" disabled={loading}>
                            <CloseIcon />
                        </IconButton>
                    </Header>

                    <Divider sx={{ my: 2 }} />

                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <Grid container spacing={2}>
                            {/* JUGADAS */}
                            <Grid item xs={12} md={6}>
                                <SectionCard>
                                    <SectionTitle>
                                        <CardGiftcardOutlinedIcon />
                                        <Typography>Jugadas</Typography>
                                    </SectionTitle>

                                    <FormControl fullWidth size="small" error={!!errors.moves}>
                                        <InputLabel>Tipo de regalo</InputLabel>
                                        <Controller
                                            control={control}
                                            name="moves"
                                            rules={{ required: 'Este campo es obligatorio.' }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    label="Tipo de regalo"
                                                    disabled={loading}
                                                >
                                                    {MOVES_OPTIONS.map((opt) => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            <Box>
                                                                <Typography sx={{ fontWeight: 700, color: '#10285f' }}>
                                                                    {opt.label}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {opt.sub}
                                                                </Typography>
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label="Fecha de fin"
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{ inputProps: { min: minDate } }}
                                        disabled={loading}
                                        error={!!errors.expires_at}
                                        helperText={errors.expires_at?.message ?? 'Opcional. Si no se define, el regalo no expira.'}
                                        sx={{ mt: 2 }}
                                        {...register('expires_at')}
                                    />
                                </SectionCard>
                            </Grid>

                            {/* USUARIOS */}
                            <Grid item xs={12} md={6}>
                                <SectionCard>
                                    <SectionTitle>
                                        <GroupsOutlinedIcon />
                                        <Typography>Usuarios</Typography>
                                    </SectionTitle>

                                    <FormControl fullWidth size="small" error={!!errors.users}>
                                        <InputLabel>Destinatarios</InputLabel>
                                        <Controller
                                            control={control}
                                            name="users"
                                            rules={{ required: 'Este campo es obligatorio.' }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    label="Destinatarios"
                                                    disabled={loading}
                                                >
                                                    {USERS_OPTIONS.map((opt) => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            <Box>
                                                                <Typography sx={{ fontWeight: 700, color: '#10285f' }}>
                                                                    {opt.label}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {opt.sub}
                                                                </Typography>
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>

                                    <HelperText>
                                        El regalo será visible solo para los usuarios que cumplan
                                        con el filtro seleccionado.
                                    </HelperText>
                                </SectionCard>
                            </Grid>

                            <Grid item xs={12}>
                                <LoadingButton
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    loading={loading}
                                    loadingPosition="start"
                                    startIcon={<SendRoundedIcon />}
                                    sx={{
                                        borderRadius: '12px',
                                        fontWeight: 900,
                                        textTransform: 'uppercase',
                                        py: 1.2,
                                        background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                        },
                                    }}
                                >
                                    <span>Crear regalo</span>
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Center>
    );
};

export default GiftsMake;

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 92%;
    max-width: 720px;
    outline: none;
`;

const Header = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 0.5rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
    }

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
`;

const SectionCard = styled.div`
    padding: 1rem;
    border-radius: 16px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: rgba(16, 40, 95, 0.025);
    height: 100%;
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 0.85rem;

    svg {
        color: #d6aa3a;
        font-size: 1.3rem;
    }

    p {
        font-weight: 900;
        color: #10285f;
        font-size: 1rem;
    }
`;

const HelperText = styled.p`
    margin: 0.6rem 0 0;
    font-size: 0.78rem;
    color: rgba(15, 34, 77, 0.6);
    line-height: 1.5;
`;
